/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { appSyncApiConfig as config } from "./config"
import { QueryResponse } from "shared/types/queryResponse"

// Define a service using a base URL and expected endpoints
export const chapterObjectsApi = createApi({
  reducerPath: "chapterObjectsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.baseUrl,
    prepareHeaders: (headers) => {
      headers.set("x-api-key", config.apiKey)

      return headers
    },
  }),
  tagTypes: ["ChapterObject"],
  endpoints: (builder) => ({
    listChapterObjectsByRefId: builder.query<any, string>({
      query: (refId) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query listChapterObjectsByRefId($eq: String = "") {
              listChapterObjects(filter: {refId: {eq: $eq}}) {
                items {
                  assignedProjectUser
                  chapterOrder
                  documentVersionId
                  name
                  id
                  isGlobal
                  isCountry
                  isProject
                  documentRefId
                  parentId
                  refId
                  sharedId
                  isIncludedInWorkflow
                  subchapterCreateAllowed
                  treeIndex
                  access
                  editing {
                    name
                    email
                    surname
                    isEditing
                  }
                  epi_status {
                    status
                    lastUpdated
                    userLastUpdated
                  }
                  client_status {
                    status
                    lastUpdated
                    userLastUpdated
                  }
                  sections {
                    content {
                      plainText
                      uploadedFiles {
                        id
                        name
                        size
                        type
                        content
                      }
                    }
                    id
                    refId
                    isProject
                    isCountry
                    isClientEditable
                    isGlobal
                    name
                    viewing
                    type
                  }
                }
              }
            }
          `,
          variables: {
            eq: refId,
          },
        },
      }),
      providesTags: ["ChapterObject"],
    }),
    createChapterObject: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation CreateChapterObject($object: CreateChapterObjectInput!) {
              createChapterObject(input: $object) {
                id
                name
                documentVersionId
                assignedProjectUser
                chapterOrder
                refId
                parentId
                sharedId
                isGlobal
                isCountry
                isProject
                documentRefId
                isIncludedInWorkflow
                subchapterCreateAllowed
                access
                sections {
                  id
                  refId
                  content {
                    plainText
                    uploadedFiles {
                      id
                      name
                      size
                      type
                      content
                    }
                  }
                  type
                  isProject
                  isCountry
                  isClientEditable
                  isGlobal
                  name
                  viewing
                }
                editing {
                  email
                  name
                  surname
                  isEditing
                }
                epi_status {
                  status
                  lastUpdated
                  userLastUpdated
                    
                }
                client_status {
                  status
                  lastUpdated
                  userLastUpdated
                }
                treeIndex
              }
            }
          `,
          variables: {
            object: { ...data },
          },
        },
      }),
      invalidatesTags: ["ChapterObject"],
    }),
    updateChapterObject: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation UpdateChapterObject($object: UpdateChapterObjectInput!) {
              updateChapterObject(input: $object) {
                id
                documentVersionId
              }
            }
          `,
          variables: {
            object: { ...data },
          },
        },
      }),
      invalidatesTags: ["ChapterObject"],
    }),
    deleteChapterObject: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation DeleteChapterObject($documentVersionId: ID = "", $id: ID = "") {
              deleteChapterObject(
                input: { documentVersionId: $documentVersionId, id: $id }
              ) {
                id
                documentVersionId
                name
              }
            }
          `,
          variables: {
            documentVersionId: data.documentVersionId,
            id: data.id,
          },
        },
      }),
      invalidatesTags: ["ChapterObject"],
    }),
    batchDeleteChapterObjects: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchDeleteChapterObjects($objects: [DeleteChapterObjectInput!]!) {
              batchDeleteChapterObjects(input: {objects: $objects}) {
                documentVersionId
                id
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
      invalidatesTags: ["ChapterObject"],
    }),
    batchUpdateChapterObjects: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchUpdateChapterObjects($objects: [UpdateChapterObjectInput!]!) {
              batchUpdateChapterObjects(input: {objects: $objects}) {
                id
                documentVersionId
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
      invalidatesTags: ["ChapterObject"],
    }),
    batchCreateChapterObjects: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchCreateChapterObjects($objects: [CreateChapterObjectInput!]!) {
              batchCreateChapterObjects(input: {objects: $objects}) {
                id
                name
                documentVersionId
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
    }),
    batchUpdateChapterSection: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchUpdateChapterSection($objects: [UpdateChapterSectionInput!]!) {
              batchUpdateChapterSection(input: {objects: $objects}) {
                id
                documentVersionId
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
    }),
    // ===================================== V1 queries =====================================
    GetChapterObjectComments: builder.query<
      QueryResponse<"getChapterObject">,
      { documentVersionId: string; id: string }
    >({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetChapterObjectComments($documentVersionId: ID = "", $id: ID = "") {
              getChapterObject(documentVersionId: $documentVersionId, id: $id) {
                id
                documentVersionId  
                epi_status {
                  comments {
                    email
                    comment
                    lastName
                    timeStamp
                    firstName
                  }
                }
                client_status {
                  comments {
                    email
                    comment
                    lastName
                    timeStamp
                    firstName
                  }
                }
              }
            }
          `,
          variables: {
            documentVersionId: params.documentVersionId,
            id: params.id,
          },
        },
      }),
      providesTags: ["ChapterObject"],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetChapterObjectCommentsQuery,
  useCreateChapterObjectMutation,
  useUpdateChapterObjectMutation,
  useBatchDeleteChapterObjectsMutation,
  useBatchUpdateChapterObjectsMutation,
  useBatchUpdateChapterSectionMutation,
  // ================ V1 ================
} = chapterObjectsApi
